import { CommonModule } from '@angular/common';
import {
  ClassProvider,
  ExistingProvider,
  ModuleWithProviders,
  NgModule,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslocoModule } from '@ngneat/transloco';
import { OregLibModule } from '@vinnova/oreg-lib';
import {
  AccordionCardModule,
  AccordionModule,
  ButtonModule,
  CheckboxesModule,
  CollapseModule,
  DatepickerModule,
  DotIndicatorModule,
  DropdownModule,
  FileUploadModule,
  IconModule,
  InteractiveTableModule,
  ListModule,
  ModalModule,
  RadiobuttonsModule,
  SafeHtmlModule,
  TextareaModule,
  TextInputModule,
  TextInputPhoneModule,
  ToggleModule,
  TooltipModule,
} from '@vinnova/ui';

import { AsideValidationItemComponent } from './components/aside-validation/aside-validation-item/aside-validation-item.component';
import { AsideValidationComponent } from './components/aside-validation/aside-validation.component';
import { DynamicFormComponent } from './components/form/dynamic-form.component';
import { QuestionAccountComponent } from './components/question/account/question-account.component';
import { BudgetComponent } from './components/question/budget/budget.component';
import { MasshanteraStodgrundComponent } from './components/question/budget/masshantera-stodgrund/masshantera-stodgrund.component';
import { ProjektbidragBudgetComponent } from './components/question/budget/projektbidrag-budget/projektbidrag-budget.component';
import { SkissBudgetComponent } from './components/question/budget/skiss-budget/skiss-budget.component';
import { UpparbetadeKostnaderComponent } from './components/question/budget/upparbetade-kostnader/upparbetade-kostnader.component';
import { QuestionCheckboxComponent } from './components/question/checkbox/question-checkbox.component';
import { ClassificationTypeComponent } from './components/question/classification/classification-type/classification-type.component';
import { QuestionClassificationComponent } from './components/question/classification/question-classification.component';
import { QuestionDatepickerComponent } from './components/question/datepicker/question-datepicker.component';
import { QuestionDatesChangeRequestComponent } from './components/question/dates-change-request/question-dates-change-request.component';
import { QuestionDropdownComponent } from './components/question/dropdown/question-dropdown.component';
import { FileUploadComponent } from './components/question/file-upload/file-upload.component';
import { GlobalGoalsComponent } from './components/question/global-goals/global-goals.component';
import { QuestionInformationComponent } from './components/question/information/question-information.component';
import { QuestionInputTableComponent } from './components/question/input-table/question-input-table.component';
import { QuestionInputComponent } from './components/question/input/question-input.component';
import { QuestionOregComponent } from './components/question/oreg/question-oreg.component';
import { QuestionPersonComponent } from './components/question/person/question-person.component';
import { QuestionContainerComponent } from './components/question/question-container.component';
import { QuestionComponent } from './components/question/question.component';
import { QuestionRadiobuttonComponent } from './components/question/radiobutton/question-radiobutton.component';
import { QuestionRepeaterComponent } from './components/question/repeater/question-repeater.component';
import { SarskildaVillkorComponent } from './components/question/sarskilda-villkor/sarskilda-villkor.component';
import { QuestionSigneringComponent } from './components/question/signering/question-signering.component';
import { QuestionTextareaComponent } from './components/question/textarea/question-textarea.component';
import { FinancierComponent } from './components/question/worksite/financier/financier.component';
import { QuestionWorksiteComponent } from './components/question/worksite/question-worksite.component';
import { SectionComponent } from './components/section/section.component';
import { AmountFormatDirective } from './directives/amount-format.directive';
import { AbsolutePipe } from './pipes/absolute.pipe';
import { CustomCurrencyPipe } from './pipes/custom-currency.pipe';
import { PhonenumberFormatPipe } from './pipes/phonenumber-format.pipe';
import { TranslateModule } from './pipes/translate/translate.module';
import { TranslatePipe } from './pipes/translate/translate.pipe';
import { TranslatedCountryPipe } from './pipes/translated-country.pipe';
import { ZipCodePipe } from './pipes/zipcode.pipe';

@NgModule({
  declarations: [
    DynamicFormComponent,
    QuestionComponent,
    SectionComponent,
    QuestionContainerComponent,
    QuestionDatepickerComponent,
    QuestionDropdownComponent,
    QuestionRadiobuttonComponent,
    QuestionTextareaComponent,
    QuestionInputComponent,
    QuestionInputTableComponent,
    QuestionRepeaterComponent,
    QuestionCheckboxComponent,
    QuestionInformationComponent,
    QuestionWorksiteComponent,
    QuestionSigneringComponent,
    AmountFormatDirective,
    CustomCurrencyPipe,
    ZipCodePipe,
    FileUploadComponent,
    QuestionPersonComponent,
    FinancierComponent,
    BudgetComponent,
    QuestionClassificationComponent,
    ClassificationTypeComponent,
    TranslatedCountryPipe,
    PhonenumberFormatPipe,
    SkissBudgetComponent,
    ProjektbidragBudgetComponent,
    UpparbetadeKostnaderComponent,
    GlobalGoalsComponent,
    QuestionOregComponent,
    QuestionAccountComponent,
    AsideValidationComponent,
    AsideValidationItemComponent,
    AbsolutePipe,
    SarskildaVillkorComponent,
    QuestionDatesChangeRequestComponent,
    MasshanteraStodgrundComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    DropdownModule,
    ReactiveFormsModule,
    CollapseModule,
    ButtonModule,
    DatepickerModule,
    TranslocoModule,
    TranslateModule,
    TooltipModule,
    FileUploadModule,
    TextInputModule,
    TextareaModule,
    CheckboxesModule,
    RadiobuttonsModule,
    TextareaModule,
    DropdownModule,
    OregLibModule,
    ListModule,
    AccordionModule,
    AccordionCardModule,
    IconModule,
    ModalModule,
    InteractiveTableModule,
    TextInputPhoneModule,
    ToggleModule,
    DotIndicatorModule,
    SafeHtmlModule,
  ],
  exports: [
    DynamicFormComponent,
    QuestionContainerComponent,
    SectionComponent,
    AsideValidationComponent,
  ],
  providers: [TranslatePipe],
})
export class VinnovaFormRendererModule {
  /**
   * @param fileServiceProvider ClassProvider used to override the default implementation of fileService (BaseFileService)
   * * @param personServiceProvider ClassProvider used to override the default implementation of personService (BasePersonService)
   * * @param translationServiceProvider ClassProvider used to override the default implementation of translationService (BaseTranslationService)
   * * @param classificationServiceProvider ClassProvider used to override the default implementation of classificationService (BaseclassificationServiceProvider)
   * * @param globalGoalsServiceProvider ClassProvider used to override the default implementation of globalGoalsService (BaseGlobalGoalsServiceProvider)
   * * * @param begaranServiceProvider ClassProvider used to override the default implementation of begaranService (BaseBegaranService)
   * @returns
   */
  public static forRoot(
    fileServiceProvider: ClassProvider,
    personDetailsProvider: ClassProvider,
    translationServiceProvider: ClassProvider,
    classificationServiceProvider: ExistingProvider,
    worksiteServiceProvider: ExistingProvider,
    globalGoalsServiceProvider: ExistingProvider,
    begaranServiceProvider: ExistingProvider,
  ): ModuleWithProviders<VinnovaFormRendererModule> {
    return {
      ngModule: VinnovaFormRendererModule,
      providers: [
        fileServiceProvider ?? undefined,
        personDetailsProvider,
        translationServiceProvider,
        classificationServiceProvider,
        worksiteServiceProvider,
        globalGoalsServiceProvider,
        begaranServiceProvider,
      ],
    };
  }
}
